import { useLocalSearchParams } from 'expo-router';
import { SlidersHorizontal } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import { FadeIn } from '../../src/components/animations';
import { HStack, Sheet, Text, VStack } from '../../src/components/primitives';
import { Button } from '../../src/components/primitives/Button';
import { useAuth } from '../../src/contexts/AuthContext';
import { CalendarMiniMonth } from '../../src/features/calendar/components/CalendarMiniMonth';
import { CalendarPeriodHeader } from '../../src/features/calendar/components/CalendarPeriodHeader';
import { CalendarReturnHrefProvider } from '../../src/features/calendar/components/CalendarReturnHref';
import { CalendarScreen } from '../../src/features/calendar/components/CalendarScreen';
import { CalendarSourceFilter } from '../../src/features/calendar/components/CalendarSourceFilter';
import { CalendarViewSwitcher } from '../../src/features/calendar/components/CalendarViewSwitcher';
import { EventFormModal } from '../../src/features/calendar/components/EventFormModal';
import { useCalendarEvents } from '../../src/features/calendar/hooks/useCalendarEvents';
import { useRescheduleCalendarEvent } from '../../src/features/calendar/hooks/useCalendarMutations';
import {
  fromNativeDate,
  type LocalDate,
  localDateKey,
  parseLocalDateKey,
} from '../../src/features/calendar/utils/calendarDate';
import {
  countEventsBySource,
  DEFAULT_EVENT_FILTER,
  type EventFilter,
  filterEvents,
} from '../../src/features/calendar/utils/calendarEventFilter';
import {
  getPeriodLabel,
  getViewDateRange,
  stepAnchor,
} from '../../src/features/calendar/utils/calendarViewRange';
import { useCalendarRealtime } from '../../src/hooks/useCalendarRealtime';
import { useLead } from '../../src/hooks/useLeads';
import { useOrgSettings } from '../../src/hooks/useOrgSettings';
import { useResponsive } from '../../src/hooks/useResponsive';
import { colors } from '../../src/theme/colors';
import { tokens } from '../../src/theme/tokens';
import type { CalendarEvent, CalendarView } from '../../src/types/calendar';

// The only entry point into the create/edit form added in Commit 20 — without
// it, useCreateCalendarEvent/useUpdateCalendarEvent and LeadInviteToggle
// (Commit 19) were unreachable from the app.
export default function CalendarScreenRoute() {
  const { currentOrganization, getAccessToken } = useAuth();
  const orgId = currentOrganization?.id;
  const { isDesktop, isWide } = useResponsive();
  const oneLineHeader = isDesktop || isWide;

  // Direct ternary, not Platform.select — Platform.select is not reliably
  // re-evaluated when a test mutates Platform.OS at runtime under this
  // project's jest-expo/web setup (see useEventPreview.ts / ChatInput.tsx).
  const [view, setView] = useState<CalendarView>(Platform.OS === 'web' ? 'month' : 'day');
  const [anchor, setAnchor] = useState<LocalDate>(() => fromNativeDate(new Date()));

  // Coming back from a lead opened out of the calendar lands here with the
  // view and day the rep was looking at. The tab stays mounted between visits,
  // so the params are applied whenever they change, not only on first render;
  // anything malformed is ignored and the current state stands.
  const params = useLocalSearchParams<{ view?: string; date?: string }>();
  const requestedView = isCalendarView(params.view) ? params.view : null;
  const requestedDateKey = parseLocalDateKey(params.date) ? params.date : null;
  useEffect(() => {
    if (requestedView) setView(requestedView);
    const requestedDate = parseLocalDateKey(requestedDateKey);
    if (requestedDate) setAnchor(requestedDate);
  }, [requestedView, requestedDateKey]);

  const returnHref = `/calendar?view=${view}&date=${localDateKey(anchor)}`;

  const range = getViewDateRange(view, anchor);

  const [filter, setFilter] = useState<EventFilter>(DEFAULT_EVENT_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);
  const periodLabel = getPeriodLabel(view, anchor);

  function goToPrevious() {
    setAnchor((prev) => stepAnchor(view, prev, -1));
  }

  function goToNext() {
    setAnchor((prev) => stepAnchor(view, prev, 1));
  }

  function goToToday() {
    setAnchor(fromNativeDate(new Date()));
  }

  // The Month grid's primary drill-down: tapping a day cell switches to Day
  // view anchored on that exact date.
  function handleSelectDay(date: LocalDate) {
    setAnchor(date);
    setView('day');
  }

  const { data: events, isLoading, error } = useCalendarEvents(orgId, range);

  // FLOSEAI-594 AC5: without this, another member's create/edit/cancel only
  // shows up here after a manual reload — this is what makes it real-time.
  useCalendarRealtime({ organizationId: orgId, enabled: !!orgId, getAccessToken });

  const { data: orgSettings } = useOrgSettings(orgId);
  const orgInviteEnabled = orgSettings?.leadInviteEnabled === true;

  // Narrowed here rather than in the query: useCalendarEvents already holds
  // every event for the visible period, so a refetch would only buy a spinner.
  const visibleEvents = useMemo(
    () => (events ? filterEvents(events, filter) : undefined),
    [events, filter],
  );
  // Counted BEFORE filtering, so a rep can see what turning a source back on
  // would actually show them.
  const sourceCounts = useMemo(() => countEventsBySource(events ?? []), [events]);

  // Desktop drag-to-reschedule. The instants arrive already resolved against
  // the event's own zone (spanToInstants, called by the block that knows the
  // day) — the route only owns the org scope.
  const reschedule = useRescheduleCalendarEvent();
  function handleReschedule(event: CalendarEvent, span: { startsAt: string; endsAt: string }) {
    if (!orgId) return;
    reschedule.mutate({ id: event.id, organizationId: orgId, ...span });
  }

  const [formVisible, setFormVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | undefined>(undefined);

  // Only populated while editing an event that already has a lead attached —
  // creating one from this screen never sets leadId, so leadProtection stays
  // null (unprotected) for every new event, same as LeadInviteToggle's own
  // "no lead attached" case.
  const { data: editingLead } = useLead(editingEvent?.leadId ?? undefined, orgId);
  const leadProtection = editingEvent?.leadId
    ? editingLead
      ? {
          doNotCall: editingLead.doNotCall === true,
          requiresReview: editingLead.requiresReview === true,
        }
      : null
    : null;

  function openCreateForm() {
    setEditingEvent(undefined);
    setFormVisible(true);
  }

  function openEditForm(event: CalendarEvent) {
    setEditingEvent(event);
    setFormVisible(true);
  }

  const title = (
    <Text variant="h4" color={colors.text.primary} style={{ fontWeight: '700' }}>
      Calendar
    </Text>
  );

  const newEventButton = (
    <Button
      variant="primary"
      size="sm"
      onPress={openCreateForm}
      accessibilityLabel="Create a new event"
    >
      New event
    </Button>
  );

  const viewSwitcher = <CalendarViewSwitcher view={view} onChange={setView} />;

  // Withheld pending a redesign of the filter itself. The sheet, the source
  // filter and the mini month all still work and are still wired — only the
  // entry point is hidden, so bringing it back is one line, not a rebuild.
  const FILTER_ENTRY_POINT_ENABLED = false;

  const filterButton = FILTER_ENTRY_POINT_ENABLED ? (
    <Button
      testID="calendar_filter_button"
      variant="outline"
      size="sm"
      accessibilityLabel="Filter events"
      onPress={() => setFilterOpen(true)}
    >
      <HStack spacing="xs" align="center">
        <SlidersHorizontal size={14} color={colors.text.primary} strokeWidth={2} />
        <Text variant="caption" color={colors.text.primary}>
          Filter
        </Text>
      </HStack>
    </Button>
  ) : null;

  const periodHeader = (
    <CalendarPeriodHeader
      label={periodLabel}
      onPrevious={goToPrevious}
      onNext={goToNext}
      onToday={goToToday}
    />
  );

  return (
    // No paddingTop: DashboardLayout already pads above content on every
    // branch (desktopContentRow + desktopContentPadding on desktop,
    // mobileContentContainer on mobile). Adding more here stacked a third
    // padding on top of those two and pushed the last week row off a short
    // laptop viewport.
    <CalendarReturnHrefProvider href={returnHref}>
      <View testID="calendar_screen" style={{ flex: 1, paddingHorizontal: tokens.spacing.lg }}>
        {/* One row on desktop and up, two when stacked. The chrome above a
          month grid is the thing competing with it for vertical space, and at
          desktop width there is room for all four controls on a single line —
          which buys back roughly a whole week row on a 768px-tall screen.
          A single FadeIn also replaces the previous staggered pair, so the
          header settles in one motion instead of two. */}
        {oneLineHeader ? (
          <FadeIn delay={0} translateY={12} style={{ marginBottom: tokens.spacing.md }}>
            <HStack
              testID="calendar_header_one_line"
              spacing="md"
              align="center"
              justify="between"
              wrap
            >
              <HStack spacing="md" align="center">
                {title}
                {viewSwitcher}
              </HStack>
              <HStack spacing="md" align="center">
                {periodHeader}
                {filterButton}
                {newEventButton}
              </HStack>
            </HStack>
          </FadeIn>
        ) : (
          <>
            <FadeIn delay={0} translateY={12} style={{ marginBottom: tokens.spacing.md }}>
              <HStack
                testID="calendar_header_stacked"
                spacing="md"
                align="center"
                justify="between"
              >
                {title}
                {newEventButton}
              </HStack>
            </FadeIn>

            <FadeIn delay={70} translateY={12} style={{ marginBottom: tokens.spacing.md }}>
              <HStack spacing="md" align="center" justify="between" wrap>
                {viewSwitcher}
                {periodHeader}
                {filterButton}
              </HStack>
            </FadeIn>
          </>
        )}

        <CalendarScreen
          view={view}
          anchor={anchor}
          isLoading={isLoading}
          error={error}
          events={visibleEvents}
          businessHours={orgSettings?.settingsOverrides?.businessHours}
          onEventPress={openEditForm}
          onReschedule={handleReschedule}
          onSelectDay={handleSelectDay}
        />

        {/* One panel for both: the filter narrows what is on screen, the mini
            month moves where the screen is looking. Neither belongs in the
            header on a phone, and the app shell has no sidebar to host them. */}
        <Sheet visible={filterOpen} onClose={() => setFilterOpen(false)} title="Filter events">
          <VStack spacing="xl">
            <CalendarMiniMonth
              anchor={anchor}
              onSelectDay={(date) => {
                setAnchor(date);
                setFilterOpen(false);
              }}
            />
            <CalendarSourceFilter filter={filter} counts={sourceCounts} onChange={setFilter} />
          </VStack>
        </Sheet>

        <EventFormModal
          visible={formVisible}
          organizationId={orgId ?? ''}
          onClose={() => setFormVisible(false)}
          event={editingEvent}
          orgInviteEnabled={orgInviteEnabled}
          leadProtection={leadProtection}
        />
      </View>
    </CalendarReturnHrefProvider>
  );
}

// Derived from a Record keyed by CalendarView, not hand-listed: a plain
// `readonly CalendarView[]` is not checked for completeness, so this guard
// silently rejected 'agenda' deep links the moment the union grew. Adding a
// view now fails to compile here until it is listed.
const CALENDAR_VIEWS = Object.keys({
  month: true,
  week: true,
  day: true,
  agenda: true,
} satisfies Record<CalendarView, true>) as readonly CalendarView[];

function isCalendarView(value: unknown): value is CalendarView {
  return typeof value === 'string' && (CALENDAR_VIEWS as readonly string[]).includes(value);
}
